// src/components/DbSchemaViewer.jsx
import React, { useState, useEffect } from 'react';
import { RefreshCw, Download, ChevronDown, ChevronUp } from 'lucide-react';

const DbSchemaViewer = () => {
  const [schema, setSchema] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedTables, setExpandedTables] = useState({});
  
  useEffect(() => {
    fetchSchema();
  }, []);
  
  const fetchSchema = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/db-schema');
      if (!response.ok) throw new Error('Failed to fetch schema');
      const data = await response.json();
      setSchema(data);
      
      // Initialize all tables as expanded
      const expanded = {};
      if (data.tables) {
        data.tables.forEach(table => {
          expanded[table] = true;
        });
      }
      setExpandedTables(expanded);
    } catch (error) {
      console.error('Error fetching schema:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  const toggleTable = (tableName) => {
    setExpandedTables(prev => ({
      ...prev,
      [tableName]: !prev[tableName]
    }));
  };
  
  const downloadSchema = () => {
    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(schema, null, 2));
    const downloadAnchorNode = document.createElement('a');
    downloadAnchorNode.setAttribute("href", dataStr);
    downloadAnchorNode.setAttribute("download", "db-schema.json");
    document.body.appendChild(downloadAnchorNode);
    downloadAnchorNode.click();
    downloadAnchorNode.remove();
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3"></div>
        <p className="text-gray-600">Loading database schema...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="p-6 bg-red-50 border border-red-200 rounded-md">
        <h2 className="text-lg font-bold text-red-700 mb-2">Error Loading Schema</h2>
        <p className="text-red-600">{error}</p>
        <button 
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          onClick={fetchSchema}
        >
          Try Again
        </button>
      </div>
    );
  }
  
  if (!schema || !schema.tables || schema.tables.length === 0) {
    return (
      <div className="p-6 bg-yellow-50 border border-yellow-200 rounded-md">
        <h2 className="text-lg font-bold text-yellow-700 mb-2">No Tables Found</h2>
        <p className="text-yellow-600">Your database doesn't appear to have any tables yet.</p>
      </div>
    );
  }
  
  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="bg-blue-600 p-4 text-white flex justify-between items-center">
        <div className="flex items-center">
          <svg className="mr-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
          </svg>
          <h1 className="text-xl font-bold">Database Schema</h1>
        </div>
        <div className="flex space-x-3">
          <button 
            onClick={downloadSchema}
            className="flex items-center px-3 py-1 bg-blue-700 rounded hover:bg-blue-800 text-sm"
          >
            <Download size={16} className="mr-1" />
            Download JSON
          </button>
          <button 
            onClick={fetchSchema}
            className="flex items-center px-3 py-1 bg-blue-700 rounded hover:bg-blue-800 text-sm"
          >
            <RefreshCw size={16} className="mr-1" />
            Refresh
          </button>
        </div>
      </div>
      
      <div className="p-4">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-lg font-medium text-gray-800">Tables ({schema.tableCount})</h2>
            <p className="text-sm text-gray-500">Schema inspection completed successfully</p>
          </div>
          <div className="text-sm text-gray-500">
            {new Date().toLocaleString()}
          </div>
        </div>
        
        <div className="space-y-4">
          {schema.tables.map(tableName => (
            <div key={tableName} className="border rounded-md overflow-hidden">
              <div 
                className="flex justify-between items-center p-3 bg-gray-100 cursor-pointer"
                onClick={() => toggleTable(tableName)}
              >
                <div className="flex items-center">
                  <svg className="w-4 h-4 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  <span className="font-medium">{tableName}</span>
                  {schema.counts && (
                    <span className="ml-2 px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded-full">
                      {schema.counts[tableName] || 0} rows
                    </span>
                  )}
                </div>
                <div className="flex items-center">
                  {expandedTables[tableName] ? (
                    <ChevronUp size={18} />
                  ) : (
                    <ChevronDown size={18} />
                  )}
                </div>
              </div>
              
              {expandedTables[tableName] && schema.schema[tableName] && (
                <div className="p-3 bg-white">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Column</th>
                          <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                          <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Not Null</th>
                          <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Default</th>
                          <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">PK</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {schema.schema[tableName] && schema.schema[tableName].columns ? 
                          schema.schema[tableName].columns.map(column => (
                            <tr key={column.name} className="hover:bg-gray-50">
                              <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900">{column.name}</td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">{column.type || 'N/A'}</td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                                {column.notnull ? (
                                  <span className="text-green-600">✓</span>
                                ) : (
                                  <span className="text-gray-400">-</span>
                                )}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                                {column.dflt_value !== null ? column.dflt_value : <span className="text-gray-400">-</span>}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                                {column.pk ? (
                                  <span className="text-blue-600 font-bold">✓</span>
                                ) : (
                                  <span className="text-gray-400">-</span>
                                )}
                              </td>
                            </tr>
                          )) : 
                          <tr>
                            <td colSpan="5" className="px-3 py-2 text-sm text-gray-500 text-center">
                              No column data available
                            </td>
                          </tr>
                        }
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Foreign Keys Section */}
                  {schema.schema[tableName].foreignKeys && schema.schema[tableName].foreignKeys.length > 0 && (
                    <div className="mt-4">
                      <h3 className="text-sm font-medium text-gray-700 mb-2">Foreign Keys</h3>
                      <div className="bg-gray-50 p-2 rounded">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead>
                            <tr>
                              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">From Column</th>
                              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">To Table</th>
                              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">To Column</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {schema.schema[tableName].foreignKeys.map((fk, index) => (
                              <tr key={index}>
                                <td className="px-3 py-2 text-sm text-gray-500">{fk.from}</td>
                                <td className="px-3 py-2 text-sm text-blue-600">{fk.table}</td>
                                <td className="px-3 py-2 text-sm text-gray-500">{fk.to}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  
                  {/* Table Creation SQL */}
                  {schema.schema[tableName].sql && (
                    <div className="mt-4">
                      <h3 className="text-sm font-medium text-gray-700 mb-2">Create Table SQL</h3>
                      <div className="bg-gray-800 text-gray-200 p-3 rounded overflow-auto text-xs">
                        <pre>{schema.schema[tableName].sql}</pre>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DbSchemaViewer;